/**
 * Creator-app appearance theme.
 *
 * Local storage is first-paint only. `users.ui_preferences.appearance.theme`
 * is the cross-device source of truth. Missing key = light.
 *
 * Marketing / public documents never receive `html.dark`.
 * Public media kits (`/media-kit/`, `/c/`) are path-exempt; the editor
 * preview opts out in-subtree via `.lh-kit-theme` so the kit theme stays
 * independent of Settings → Appearance.
 */
import { getDesktopBridge, isDesktopApp } from '@/desktop/bridge'

export const THEME_STORAGE_KEY = 'lh-appearance-theme'
export const THEME_SYNC_CHANNEL = 'lh-appearance-theme'

export const THEME_PAGE_LIGHT = '#fafafa'
export const THEME_PAGE_DARK = '#0c0a09'

export type ThemePreference = 'light' | 'dark' | 'system'
export type ResolvedTheme = 'light' | 'dark'

const THEME_PREFERENCES: readonly ThemePreference[] = ['light', 'dark', 'system']

/** Exact public/marketing paths that stay light. */
const THEME_EXEMPT_EXACT = new Set([
    '/',
    '/launch',
    '/our-story',
    '/press',
    '/ambassador',
    '/pricing-guide',
    '/privacy',
    '/terms',
    '/support',
    '/delete-account',
    '/changelog',
    '/updates',
    '/inbox',
    '/deals',
    '/assistant',
    '/lp',
    '/meet',
    '/netscape',
    '/content',
    '/login',
    '/keynote',
])

/**
 * In the native app the marketing pages are unreachable (authGuard) and `/` is only the start URL the WebView
 * loads before routing, so exempting it painted every cold start light. Only the public documents that carry
 * their own look stay exempt there.
 */
const NATIVE_THEME_EXEMPT_PREFIXES = ['/media-kit/', '/c/', '/draft/']

/** Prefixes for marketing, public kits, and other isolated documents. */
const THEME_EXEMPT_PREFIXES = [
    '/launch/',
    '/lp/',
    '/media-kit/',
    '/c/',
    '/updates/',
    '/demo/',
    '/draft/',
    '/meet/',
    '/r/',
]

export function isThemePreference(value: unknown): value is ThemePreference {
    return typeof value === 'string' && THEME_PREFERENCES.includes(value as ThemePreference)
}

/** Missing or invalid preference resolves to light. */
export function normalizeThemePreference(value: unknown): ThemePreference {
    return isThemePreference(value) ? value : 'light'
}

export function readThemePreferenceFromPrefs(
    uiPreferences?: Record<string, any> | null,
): ThemePreference {
    return normalizeThemePreference(uiPreferences?.appearance?.theme)
}

export function readStoredThemePreference(): ThemePreference {
    try {
        return normalizeThemePreference(localStorage.getItem(THEME_STORAGE_KEY))
    }
    catch {
        return 'light'
    }
}

export function writeStoredThemePreference(preference: ThemePreference) {
    try {
        localStorage.setItem(THEME_STORAGE_KEY, preference)
    }
    catch {
        // Private mode / blocked storage — first paint may flash; server still wins.
    }
}

export function resolveThemePreference(
    preference: ThemePreference,
    systemPrefersDark: boolean,
): ResolvedTheme {
    if (preference === 'dark') return 'dark'
    if (preference === 'system') return systemPrefersDark ? 'dark' : 'light'
    return 'light'
}

/** ⌘⇧D / Ctrl+Shift+D — common dark-mode toggle; avoids TipTap's ⌘⇧L align. */
export function isThemeToggleHotkey(event: {
    key?: string
    code?: string
    metaKey?: boolean
    ctrlKey?: boolean
    shiftKey?: boolean
    altKey?: boolean
    repeat?: boolean
    isComposing?: boolean
}): boolean {
    if (event.repeat || event.isComposing) return false
    if (!event.shiftKey || event.altKey) return false
    if (!(event.metaKey || event.ctrlKey)) return false
    const key = (event.key || '').toLowerCase()
    return key === 'd' || event.code === 'KeyD'
}

/** Explicit light/dark opposite of the currently resolved theme. */
export function oppositeResolvedTheme(resolved: ResolvedTheme): Exclude<ThemePreference, 'system'> {
    return resolved === 'dark' ? 'light' : 'dark'
}

/** Public / marketing documents that stay light. `native`: the app's rule (only the public kits and drafts). */
export function isThemeExemptPath(path: string, native = false): boolean {
    const pathname = (path || '/').split('?')[0] || '/'
    if (native) return NATIVE_THEME_EXEMPT_PREFIXES.some((prefix) => pathname.startsWith(prefix))
    if (THEME_EXEMPT_EXACT.has(pathname)) return true
    return THEME_EXEMPT_PREFIXES.some((prefix) => pathname.startsWith(prefix))
}

/** Whether this document, here (web or the native shell), keeps the light theme on `path`. */
export function isThemeExemptHere(path: string): boolean {
    return isThemeExemptPath(path, isNativeShell())
}

/** The Capacitor shell, read straight off the injected bridge (this module stays free of plugin imports). */
function isNativeShell(): boolean {
    if (typeof window === 'undefined') return false
    const cap = (window as Window & { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor
    try {
        return !!cap?.isNativePlatform?.()
    }
    catch {
        return false
    }
}

export function documentThemeIsDark(): boolean {
    return typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
}

export function calEmbedUi(theme: ResolvedTheme) {
    return {
        theme,
        cssVarsPerTheme: {
            light: { 'cal-brand': '#FB4C1F' },
            dark: { 'cal-brand': '#FB4C1F' },
        },
    } as const
}

function setMetaContent(name: string, content: string, target: Document) {
    let meta = target.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null
    if (!meta) {
        meta = target.createElement('meta')
        meta.setAttribute('name', name)
        target.head?.appendChild(meta)
    }
    meta.setAttribute('content', content)
}

let lastDesktopAppearance = ''

/** Desktop app: the native window paints this colour before content loads and picks light / dark chrome. */
function syncDesktopChrome(resolved: ResolvedTheme, preference: ThemePreference, page: string) {
    const bridge = getDesktopBridge()
    if (!bridge) return
    const key = `${resolved}|${preference}|${page}`
    if (key === lastDesktopAppearance) return
    lastDesktopAppearance = key
    try {
        bridge.chrome.setAppearance({ theme: resolved, preference, page })
    }
    catch {
        // Older shell without appearance support.
    }
}

let nativeChromeTheme: ResolvedTheme | null = null

/**
 * Status bar (and on Android the navigation bar) content colour for the theme. Only when the resolved theme
 * actually changes: the theme is re-applied on navigation, and each sync is two or three bridge calls.
 */
async function syncNativeChrome(resolved: ResolvedTheme) {
    if (!isNativeShell() || nativeChromeTheme === resolved) return
    nativeChromeTheme = resolved
    try {
        const { StatusBar, Style } = await import('@capacitor/status-bar')
        await StatusBar.setOverlaysWebView({ overlay: true })
        await StatusBar.setStyle({ style: resolved === 'dark' ? Style.Dark : Style.Light })
    }
    catch {
        // Plugin unavailable.
    }
    try {
        const { Capacitor, SystemBars, SystemBarsStyle, SystemBarType } = await import('@capacitor/core')
        if (Capacitor.getPlatform() === 'android' && Capacitor.isPluginAvailable('SystemBars')) {
            await SystemBars.setStyle({
                style: resolved === 'dark' ? SystemBarsStyle.Dark : SystemBarsStyle.Light,
                bar: SystemBarType.NavigationBar,
            })
        }
    }
    catch {
        // Capacitor < 8 (no SystemBars): the navigation bar keeps the system default.
    }
}

/** The status bar style was set elsewhere (boot, the auth keyboard): sync on the next apply. */
export function invalidateNativeChrome() {
    nativeChromeTheme = null
}

/**
 * Apply the resolved theme to the document (and native chrome). `preference`
 * is what the creator picked (light / dark / system), for the desktop shell.
 */
export function applyResolvedTheme(resolved: ResolvedTheme, target?: Document, preference?: ThemePreference) {
    const doc = target ?? (typeof document !== 'undefined' ? document : undefined)
    if (!doc) return

    const root = doc.documentElement
    const page = resolved === 'dark' ? THEME_PAGE_DARK : THEME_PAGE_LIGHT

    root.classList.toggle('dark', resolved === 'dark')
    root.style.colorScheme = resolved
    root.style.backgroundColor = page
    if (doc.body) {
        doc.body.style.backgroundColor = page
        doc.body.style.colorScheme = resolved
    }

    setMetaContent('color-scheme', resolved, doc)
    setMetaContent('theme-color', page, doc)
    setMetaContent(
        'apple-mobile-web-app-status-bar-style',
        resolved === 'dark' ? 'black-translucent' : 'default',
        doc,
    )

    if (typeof document !== 'undefined' && doc === document) {
        if (isDesktopApp()) syncDesktopChrome(resolved, preference ?? readStoredThemePreference(), page)
        else void syncNativeChrome(resolved)
    }
}

/** Apply a preference to another same-origin document (e.g. the onboarding preview iframe). */
export function applyThemePreferenceToDocument(
    preference: ThemePreference,
    target: Document,
    systemPrefersDark?: boolean,
) {
    const systemDark = systemPrefersDark
        ?? target.defaultView?.matchMedia('(prefers-color-scheme: dark)').matches
        ?? false
    applyResolvedTheme(resolveThemePreference(preference, systemDark), target)
}
